import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  QaiFloatingSelectComponent,
  QaiFieldComponent,
  QaiInnerWrapComponent,
  QaiFloatingSelectOption,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { LeaderboardLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { LeaderboardService } from 'src/app/services/leaderboard/leaderboard.service';
import { IAssignment } from 'src/app/interfaces/leaderboard/iassignment';

import {
  ISimulation,
  ISimulations,
} from 'src/app/interfaces/leaderboard/isimulation';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';

@Component({
  selector: 'app-leaderboard-header',
  templateUrl: './leaderboard-header.component.html',
  styleUrls: ['./leaderboard-header.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'leaderboard',
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiFieldComponent,
    QaiFloatingSelectComponent,
    QaiInnerWrapComponent,
  ],
})
export class LeaderboardHeaderComponent implements OnInit {
  @Output() assignmentSelected = new EventEmitter<IAssignment | null>();
  @Output() simulationSelected = new EventEmitter<ISimulation | null>();

  isTranslationLoaded: boolean = false;

  assignmentOptions: QaiFloatingSelectOption[] = [];
  simulationOptions: QaiFloatingSelectOption[] = [];

  selectedAssignment: string = '';
  selectedSimulation: string = '';

  private assignmentsLoaded: boolean = false;

  constructor(
    private translationService: TranslationService,
    private leaderboardService: LeaderboardService,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('leaderboard').then(() => {
      this.isTranslationLoaded = true;
    });

    this.loadAssignments();
  }

  private loadAssignments(): void {
    const accountUuid = localStorage.getItem('current_account_uuid');

    if (!accountUuid) {
      console.error('No account UUID found in localStorage');
      this.assignmentOptions = [];
      return;
    }

    this.leaderboardService.getAssignmentByAccount(accountUuid, withDemoModule(AppConstants.LEADERBOARD_UI)).subscribe({
      next: (assignments: IAssignment[]) => {
        this.assignmentOptions = assignments
          .map((assignment) => ({
            value: assignment.moduleAssignmentUuid,
            label: assignment.moduleAssignmentName,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        this.assignmentsLoaded = true;
        this.loadPersistedSelections();
      },
      error: (error) => {
        console.error('Failed to load assignments:', error);
        this.assignmentOptions = [];
        this.assignmentsLoaded = true;
      },
    });
  }

  private loadSimulations(assignmentUuid: string): void {
    const accountUuid = localStorage.getItem('current_account_uuid');

    if (!accountUuid) {
      console.error('No account UUID found in localStorage');
      this.simulationOptions = [];
      return;
    }

    this.leaderboardService
      .getSimulationByModuleAssignment(assignmentUuid, accountUuid, withDemoModule(AppConstants.LEADERBOARD_UI))
      .subscribe({
        next: (simulations: ISimulation[]) => {
          this.simulationOptions = simulations
            .map((simulation) => ({
              value: simulation.callUuid,
              label: simulation.callName,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          if (this.simulationOptions.length > 0) {
            const persistedSimulation =
              LeaderboardLocalStorageUtil.getSelectedSimulationUuid();
            if (!persistedSimulation) {
              this.selectFirstSimulation();
            } else {
              this.loadPersistedSimulationSelection();
            }
          } else {
            this.selectedSimulation = '';
            this.simulationSelected.emit(null);
          }
        },
        error: (error) => {
          console.error('Failed to load simulations:', error);
          this.simulationOptions = [];
        },
      });
  }

  private loadPersistedSelections(): void {
    try {
      const persistedAssignment =
        LeaderboardLocalStorageUtil.getSelectedAssignmentUuid();
      if (
        persistedAssignment &&
        this.isValidAssignmentOption(persistedAssignment)
      ) {
        this.selectedAssignment = persistedAssignment;
        this.loadSimulations(persistedAssignment);
        this.emitAssignmentSelection(persistedAssignment);
      } else if (this.assignmentOptions.length > 0) {
        this.selectFirstAssignment();
      }
    } catch (error) {
      console.error('Error loading persisted selections:', error);
      LeaderboardLocalStorageUtil.clearAllLeaderboardSelections();
    }
  }

  private selectFirstAssignment(): void {
    if (this.assignmentOptions.length > 0) {
      LeaderboardLocalStorageUtil.setSelectedAssignmentUuid(
        this.assignmentOptions[0].value
      );
      this.selectedAssignment = this.assignmentOptions[0].value;
      this.loadSimulations(this.assignmentOptions[0].value);
      this.emitAssignmentSelection(this.assignmentOptions[0].value);
    }
  }

  private selectFirstSimulation(): void {
    if (this.simulationOptions.length > 0) {
      LeaderboardLocalStorageUtil.setSelectedSimulationUuid(
        this.simulationOptions[0].value
      );
      this.selectedSimulation = this.simulationOptions[0].value;
      this.simulationSelected.emit({
        callUuid: this.simulationOptions[0].value,
        callName: this.simulationOptions[0].label,
      });
    }
  }

  private loadPersistedSimulationSelection(): void {
    try {
      if (this.selectedAssignment) {
        const persistedSimulation =
          LeaderboardLocalStorageUtil.getSelectedSimulationUuid();
        if (
          persistedSimulation &&
          this.isValidSimulationOption(persistedSimulation)
        ) {
          this.selectedSimulation = persistedSimulation;
          this.emitSimulationSelection(persistedSimulation);
        } else {
          LeaderboardLocalStorageUtil.clearSelectedSimulationUuid();
        }
      }
    } catch (error) {
      console.error('Error loading persisted simulation selection:', error);
      LeaderboardLocalStorageUtil.clearSelectedSimulationUuid();
    }
  }

  private isValidAssignmentOption(assignmentUuid: string): boolean {
    return this.assignmentOptions.some(
      (option) => option.value === assignmentUuid
    );
  }

  private isValidSimulationOption(simulationUuid: string): boolean {
    return this.simulationOptions.some(
      (option) => option.value === simulationUuid
    );
  }

  valueChange(event: any): void {
    const { value, source } = event;

    if (source === 'assignment') {
      this.handleAssignmentChange(value);
    } else if (source === 'simulation') {
      this.handleSimulationChange(value);
    }
  }

  private handleAssignmentChange(value: string): void {
    this.selectedAssignment = value;

    if (value) {
      LeaderboardLocalStorageUtil.setSelectedAssignmentUuid(value);
      LeaderboardLocalStorageUtil.clearSelectedSimulationUuid();
      this.loadSimulations(value);
      this.emitAssignmentSelection(value);
      this.selectedSimulation = '';
      this.simulationSelected.emit(null);
    } else {
      LeaderboardLocalStorageUtil.clearSelectedAssignmentUuid();
      this.resetSimulationSelection();
    }
  }

  private handleSimulationChange(value: string): void {
    this.selectedSimulation = value;

    if (value) {
      LeaderboardLocalStorageUtil.setSelectedSimulationUuid(value);
      this.emitSimulationSelection(value);
    } else {
      LeaderboardLocalStorageUtil.clearSelectedSimulationUuid();
      this.simulationSelected.emit(null);
    }
  }

  private emitAssignmentSelection(value: string): void {
    const assignment = this.assignmentOptions.find(
      (opt) => opt.value === value
    );
    if (assignment) {
      this.amplitudeService.trackEvent(
        AmplitudeTrackingEvents.LEADERBOARD_CLICK_ASSIGNMENT,
        {
          assignment_name: assignment.label,
        }
      );

      this.assignmentSelected.emit({
        moduleAssignmentUuid: assignment.value,
        moduleAssignmentName: assignment.label,
      });
    }
  }

  private emitSimulationSelection(value: string): void {
    const simulation = this.simulationOptions.find(
      (opt) => opt.value === value
    );
    if (simulation) {
      this.amplitudeService.trackEvent(
        AmplitudeTrackingEvents.LEADERBOARD_CLICK_SIMULATION,
        {
          simulation_name: simulation.label,
        }
      );

      this.simulationSelected.emit({
        callUuid: simulation.value,
        callName: simulation.label,
      });
    }
  }

  private resetSimulationSelection(): void {
    this.simulationOptions = [];
    this.selectedSimulation = '';
    this.assignmentSelected.emit(null);
    this.simulationSelected.emit(null);
  }

  getAssignmentPlaceholder(): string {
    if (!this.assignmentsLoaded) {
      return this.translationService.translate('leaderboard.SELECT_ASSIGNMENT');
    }

    if (this.assignmentOptions.length === 0) {
      return this.translationService.translate(
        'leaderboard.NO_ASSIGNMENTS_AVAILABLE'
      );
    }

    return this.translationService.translate('leaderboard.SELECT_ASSIGNMENT');
  }

  getSimulationPlaceholder(): string {
    if (!this.selectedAssignment) {
      return this.translationService.translate('leaderboard.SELECT_SIMULATION');
    }

    if (this.simulationOptions.length === 0) {
      return this.translationService.translate(
        'leaderboard.NO_SIMULATIONS_AVAILABLE'
      );
    }

    return this.translationService.translate('leaderboard.SELECT_SIMULATION');
  }
}
